/**
 * Working-day math for the scheduling heatmap.
 *
 * Context (T2-65): the heatmap distributes a task's forecast across a person's working days as
 * `forecast * workingDaysInLoop / workingDaysInTask`. Two inputs were unguarded, letting NEGATIVE
 * allocations surface on the People Schedule:
 *   1. A structurally non-working day (working minutes === 0) was counted as "time off" whenever a
 *      time-off allocation carried any minutes on it (e.g. HiBob-synced PTO on a day off).
 *   2. `workingDays - dayOffDays` was never clamped, so the count could go negative. Holiday
 *      calendar entries are added to the day-off set for every day they land on, so this clamp —
 *      not (1) — was the universal guard.
 *
 * Since PIKA-736 the clamp no longer fires on any production path. Every call site
 * (`HeatmapLogic.js`'s two, `shared/effortDistribution.js`, and the server port's
 * `EffortDistributionSupportLogic.js`) pairs a working-day count built from `personWorkingMinutes`
 * with a day-off count over the same window that is filtered to the person's working days —
 * `getNonWorkingDays` filters unconditionally once given `personWorkingMinutes`, and the
 * `inverted_pto_non_working_days` branch counts `holidaysExcludingDaysOff`, which is filtered and
 * deduped at construction. So the subtrahend can no longer exceed the minuend and the result is
 * already non-negative. That also closes a second defect: before the filter, with
 * `combined_mode_performance_improvements` off, the clamp *did* fire, and clamping a per-loop count
 * to 0 while the task-wide denominator kept its own (differently over-subtracted) value pushed
 * total allocation above `timeLeft`. Both are now fixed at the source. The clamp stays as
 * defence-in-depth for a future caller that pairs the two counts differently — it should not be
 * relied on.
 */

// A time-off allocation only turns a day into a full day off if the person actually works that day.
// On a non-working day (workingMinutesOnDay === 0) time off must NOT count, or the day gets
// double-counted and downstream working-day counts go negative.
export const isFullDayOff = (workingMinutesOnDay: number, allocationMinutesOnDay: number): boolean =>
	workingMinutesOnDay > 0 && allocationMinutesOnDay >= workingMinutesOnDay;

// Working days left after removing day-off (time-off / holiday) days. Never negative.
export const getWorkingDayCount = (workingDaysInRange: number, dayOffDaysInRange: number): number =>
	Math.max(0, workingDaysInRange - dayOffDaysInRange);
